import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';

const ORG_TYPES = ['partner', 'funder', 'government', 'ngo', 'university', 'media', 'corporate', 'other'];

// Searchable dropdown for selecting an organisation. Includes an inline
// "+ Add new organisation" form (just name + type) that creates via the API
// and auto-selects the new row.
//
// Props:
//   value: organisation id or null
//   organisations: array fetched from api.organisations.list() (parent-managed cache)
//   onChange: (newId) => void
//   onCreated: (newOrg) => void   parent should refresh its org list
export function OrgPicker({ value, organisations, onChange, onCreated }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('partner');
  const [creating, setCreating] = useState(false);
  const ref = useRef(null);

  const selected = organisations.find((o) => o.id === value);

  useEffect(() => {
    if (!open) return undefined;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setShowCreateForm(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = organisations.filter((o) =>
    o.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  function selectOrg(id) {
    onChange(id);
    setOpen(false);
    setQuery('');
    setShowCreateForm(false);
  }

  function startCreate() {
    setNewName(query || '');
    setShowCreateForm(true);
  }

  async function handleCreate(e) {
    e.preventDefault();
    e.stopPropagation();
    const trimmed = newName.trim();
    if (!trimmed || creating) return;
    setCreating(true);
    try {
      const created = await api.organisations.create({ name: trimmed, type: newType });
      onCreated?.(created);
      onChange(created.id);
      setOpen(false);
      setShowCreateForm(false);
      setQuery('');
      setNewName('');
      setNewType('partner');
    } catch (err) {
      console.error('[org-picker] create failed:', err);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="org-picker" ref={ref}>
      <button
        type="button"
        className="org-picker__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="org-picker__trigger-name">
          {selected ? (
            <>
              {selected.name}{' '}
              <span className={`org-type-chip org-type-chip--${selected.type}`} style={{ marginLeft: 4 }}>
                {t(`organisations.types.${selected.type}`)}
              </span>
            </>
          ) : (
            <span className="org-picker__trigger-placeholder">{t('orgPicker.placeholder')}</span>
          )}
        </span>
        <span className="org-picker__caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="org-picker__menu" role="listbox">
          <input
            className="org-picker__search"
            type="text"
            value={query}
            placeholder={t('orgPicker.search')}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />

          <ul className="org-picker__list">
            {value && (
              <li>
                <button
                  type="button"
                  className="org-picker__item"
                  onClick={() => selectOrg(null)}
                >
                  <span className="org-picker__item-name" style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                    {t('orgPicker.noOrg')}
                  </span>
                </button>
              </li>
            )}
            {filtered.length === 0 ? (
              <li className="org-picker__no-match">{t('orgPicker.noMatch')}</li>
            ) : (
              filtered.map((org) => (
                <li key={org.id}>
                  <button
                    type="button"
                    className={
                      'org-picker__item' + (org.id === value ? ' org-picker__item--selected' : '')
                    }
                    onClick={() => selectOrg(org.id)}
                  >
                    <span className="org-picker__item-name">{org.name}</span>
                    <span className={`org-type-chip org-type-chip--${org.type}`}>
                      {t(`organisations.types.${org.type}`)}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>

          {showCreateForm ? (
            <div className="org-picker__inline-form">
              <input
                type="text"
                value={newName}
                placeholder={t('orgPicker.inlineCreate.name')}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
              />
              <select value={newType} onChange={(e) => setNewType(e.target.value)}>
                {ORG_TYPES.map((typeKey) => (
                  <option key={typeKey} value={typeKey}>
                    {t(`organisations.types.${typeKey}`)}
                  </option>
                ))}
              </select>
              <div className="org-picker__inline-form-actions">
                <button
                  type="button"
                  className="button--ghost"
                  onClick={() => setShowCreateForm(false)}
                  disabled={creating}
                >
                  {t('orgPicker.inlineCreate.cancel')}
                </button>
                <button
                  type="button"
                  className="button--primary"
                  onClick={handleCreate}
                  disabled={creating || !newName.trim()}
                >
                  {creating ? '…' : t('orgPicker.inlineCreate.create')}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="org-picker__add" onClick={startCreate}>
              {t('orgPicker.addNew')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
